import { useLazyGetLatestVersionQuery, useRestoreSettingsMutation } from '@oneview/api';
import { apiConfig, appConfig } from '@oneview/config';
import { useCurrentUser } from '@oneview/hooks';
import { selectAuth } from '@oneview/redux/auth';
import { selectGlobalSettings } from '@oneview/redux/settings/settingSelectors';
import { useDispatch } from '@oneview/redux/utils';
import { ErrorBoundary } from '@oneview/shared/ErrorBoundary';
import { isDevelopment } from '@oneview/utils';
import { showToast } from '@/redux';
import { Loader } from '@oneview/shared/Loader';
import { AsyncActionStatus } from '@/constants/lookups';
import { AlertDialogHandler } from '@oneview/shared/AlertDialogHandler';
import { CacheBuster } from '@oneview/shared/CacheBuster';
import { LoaderHandler } from '@oneview/shared/LoaderHandler';
import { OrtkProviders } from '@oneview/shared/OrtkProviders';
import { ToastHandler } from '@oneview/shared/ToastHandler';
import { UsersnapProvider } from '@oneview/shared/UsersnapContext';
import React, { Suspense, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Route, Routes } from 'react-router-dom';
import { useMuiTheme } from '../hooks/use-mui-theme';
import { Route as AppRoute } from '../constants/routes';
import { DialogProvider } from './~shared/useDialog';
import { Toaster } from '@/components/ui/sonner';
import { useTheme } from './theme-provider';

const Login = React.lazy(() => import('./Login').then((module) => ({ default: module.Login })));
const LoginSso = React.lazy(() => import('./Login').then((module) => ({ default: module.LoginSso })));
const Main = React.lazy(() => import('./Main').then((module) => ({ default: module.Main })));

export const App: React.FunctionComponent = () => {
    const dispatch = useDispatch();
    const { isLoggedIn } = useSelector(selectAuth);
    const user = useCurrentUser();
    const muiTheme = useMuiTheme();
    const globalSettings = useSelector(selectGlobalSettings);
    const { setTheme } = useTheme();

    const [getLatestVersion] = useLazyGetLatestVersionQuery();
    const [restoreSettings] = useRestoreSettingsMutation();

    useEffect(() => {
        const restore = async () => {
            try {
                await restoreSettings().unwrap();
            } catch {
                dispatch(showToast('Failed to get user preferences'));
            }
        };

        if (isLoggedIn && user.userId) {
            restore();
        }
    }, [dispatch, isLoggedIn, restoreSettings, user.userId]);

    useEffect(() => {
        if (isLoggedIn) setTheme(globalSettings.useDarkTheme ? 'dark' : 'light');
    }, [globalSettings.useDarkTheme, isLoggedIn, setTheme]);

    return (
        <OrtkProviders theme={muiTheme}>
            <UsersnapProvider
                apiKey={apiConfig.usersnapSpaceApiKey}
                init={{
                    user: {
                        email: String(user.email),
                        userId: String(user.userId),
                    },
                    custom: {
                        appVersion: appConfig.appVersion,
                        buildNumber: appConfig.appBuildNumber,
                    },
                }}
            >
                <ErrorBoundary useDialog>
                    <AlertDialogHandler />
                    <Toaster
                        visibleToasts={10}
                        closeButton
                        toastOptions={{
                            duration: 10000,
                            classNames: {
                                toast: 'group toast group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg',
                                description: 'group-[.toast]:!text-muted-foreground',
                                closeButton: 'group-[.toast]:!text-primary hover:!bg-secondary',
                            },
                        }}
                    />
                    <ToastHandler />
                    <LoaderHandler />
                    <DialogProvider>
                        <CacheBuster
                            disabled={isDevelopment()}
                            newVersion={() => getLatestVersion().unwrap()}
                            currentVersion={appConfig.appBuildNumber}
                        />
                        <Suspense fallback={<Loader status={AsyncActionStatus.Pending} />}>
                            <Routes>
                                <Route path={AppRoute.Login} element={<Login />} />
                                <Route path={AppRoute.LoginSso} element={<LoginSso />} />
                                <Route path="*" element={<Main />} />
                            </Routes>
                        </Suspense>
                    </DialogProvider>
                </ErrorBoundary>
            </UsersnapProvider>
        </OrtkProviders>
    );
};
